import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import type { ActionsObservable } from 'redux-observable';
import get from 'lodash/get';
import { Observable } from 'rxjs/Observable';
import log from '@atlassian/jira-common-util-logging/src/log';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import triggerAnalyticsEvent from '@atlassian/jira-servicedesk-automation-common/src/analytics';
import type { AnError } from '@atlassian/jira-servicedesk-common/src/model/error/anerror';
import type { SummaryPage } from '../../model';
import getSummary from '../../services/get';
import {
	RETRIEVE,
	type SummaryLoadRequestAction,
	summaryLoadSuccess,
	type SummaryLoadSuccessAction,
	summaryLoadFailure,
	type SummaryLoadFailureAction,
} from '../../state/actions/retrieve';
import type { State } from '../../state/types';

const handleFailure = (
	action: SummaryLoadRequestAction,
	error: AnError,
): SummaryLoadFailureAction => summaryLoadFailure(error);

const handleSuccess = (
	action: SummaryLoadRequestAction,
	result: SummaryPage,
): SummaryLoadSuccessAction => {
	triggerAnalyticsEvent('summary.view');

	if (!get(result, ['result', 'projectRunAsUserSettings', 'config', 'user', 'isValid'])) {
		triggerAnalyticsEvent('summary.loaded.misconfigured-user');
	}
	return summaryLoadSuccess(result);
};

const isNetworkError = (error: { response: AnError }) =>
	// fetchJson$ returns errors in this shape when there's a connectivity issue
	error &&
	error.response &&
	error.response.reasonKey === 'generic.error' &&
	error.response.reasonCode === 'undefined';

const isExpectedClientError = (error: { response: AnError }) =>
	error && error.response && error.response.reasonCode === '403';

// eslint-disable-next-line @typescript-eslint/no-explicit-any, jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<any>, store: MiddlewareAPI<State>) =>
	action$.ofType(RETRIEVE).switchMap((action) =>
		getSummary(store.getState())
			.map((result) => handleSuccess(action, result))
			.catch((error) => {
				if (!isNetworkError(error) && !isExpectedClientError(error)) {
					log.unsafeErrorWithCustomerData(
						'jiraServicedeskAutomationSummary.ops.get',
						`Error when loading automation summary, details:${JSON.stringify(error)}`,
					);
					fireErrorAnalytics({
						meta: {
							id: 'summaryLoadFailure',
							packageName: 'jiraServicedeskAutomationSummary',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
				return Observable.of(handleFailure(action, error.response));
			}),
	);
